// services/FirebaseService.js
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, getDocs, addDoc, query, where, orderBy, doc, setDoc, getDoc } from 'firebase/firestore';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: "AIzaSyAysU3cfGKC1Ogii173gO1zNaAQOsizTJQ",
  authDomain: "daisies-70154.firebaseapp.com",
  projectId: "daisies-70154",
  storageBucket: "daisies-70154.appspot.com",
  messagingSenderId: "772742382225",
  appId: "1:772742382225:web:47643d98a6f8b0135f3530",
  measurementId: "G-1XNTTQMZCS"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app); // ← ESTA ES LA LÍNEA QUE FALTABA

/**
 * Source of truth para "el correo está verificado": Firebase Auth.
 * Recarga el currentUser desde Firebase y devuelve si su emailVerified es true.
 * Mirroring de lib/auth/auth_manager.refreshUser() + currentUserEmailVerified en flutterUXS.
 */
/**
 * Devuelve un ID token de Firebase FRESCO del usuario actual. Los ID tokens
 * expiran a la hora; en flujos largos (p.ej. el cuestionario) el token guardado
 * en sesión llega vencido y Odoo rechaza la llamada. Esto fuerza la renovación.
 * @returns {string|null} token vigente o null si no hay sesión Firebase.
 */
export const getFreshIdToken = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return null;
    return await user.getIdToken();
  } catch (err) {
    console.error('[FirebaseService] getFreshIdToken error:', err);
    return null;
  }
};

export const isFirebaseEmailVerified = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return { success: false, verified: false, error: 'No hay sesión Firebase activa' };
    await user.reload();
    return { success: true, verified: !!auth.currentUser?.emailVerified };
  } catch (err) {
    return { success: false, verified: false, error: err?.message || 'No se pudo recargar el usuario Firebase' };
  }
};

const testCategoryMap = {
  'ansiedad': 12, 
  'terapia': 11,
  'burnout': 13,
  'autoestima': 14,
  'salud-emocional-1': 15,
  'salud-emocional-2': 16
};

export async function createUserInFirebase(userData) {
  try {
    
    // 1. Crear en Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(
      auth, 
      userData.email, 
      userData.password
    );
    
    const user = userCredential.user;
    const uid = user.uid; // Este es el ID único de Firebase

    // 2. Crear el perfil en la colección 'users'
    // Usamos doc(db, 'users', uid) para que el ID del documento sea el UID del usuario
    await setDoc(doc(db, 'users', uid), {
      uid: uid,
      email: userData.email.toLowerCase().trim(),
    });

    
    const firebaseToken = await user.getIdToken();
    
    return {
      success: true,
      firebaseUid: uid,
      firebaseToken: firebaseToken,
      email: user.email
    };
    
  } catch (error) {
    return {
      success: false,
      error: error.message,
      errorCode: error.code
    };
  }
}

/**
 * Login de usuario en Firebase
 */
export const loginUserInFirebase = async (email, password) => {
  try {
    
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Obtener token
    const firebaseToken = await user.getIdToken();
    
    
    return {
      success: true,
      firebaseUid: user.uid,
      firebaseToken: firebaseToken,
      email: user.email
    };
    
  } catch (error) {
    
    let errorMessage = 'Error al iniciar sesión';
    
    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = 'Usuario no encontrado';
        break;
      case 'auth/wrong-password':
        errorMessage = 'Contraseña incorrecta';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Email inválido';
        break;
      case 'auth/user-disabled':
        errorMessage = 'Usuario deshabilitado';
        break;
      default:
        errorMessage = error.message;
    }
    
    return {
      success: false,
      error: errorMessage,
      errorCode: error.code
    };
  }
};

/**
 * Envía el correo de restablecimiento de contraseña (Firebase Auth).
 * Réplica del flujo de flutterUXS (FirebaseAuth.sendPasswordResetEmail).
 */
export const sendPasswordResetInFirebase = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email.trim());
    return { success: true };
  } catch (error) {
    let errorMessage = 'No pudimos enviar el correo. Intenta de nuevo.';
    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = 'No encontramos una cuenta con ese correo.';
        break;
      case 'auth/invalid-email':
        errorMessage = 'El correo electrónico no es válido.';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Demasiados intentos. Espera un momento e intenta de nuevo.';
        break;
      default:
        errorMessage = error.message || errorMessage;
    }
    return { success: false, error: errorMessage, errorCode: error.code };
  }
};

// ========================================
// FUNCIONES DE FIRESTORE
// ========================================

export const getTestsFromFirebase = async () => {
  try {
    
    const testsRef = collection(db, 'dais_health_questions');
    const testsSnapshot = await getDocs(testsRef);
    
    const tests = testsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return tests;
  } catch (error) {
    throw error;
  }
};

export const getQuestionsByTestId = async (testId) => {
  try {
    
    const categoryId = testCategoryMap[testId];
    if (!categoryId) {
      return [];
    }

    const questionsRef = collection(db, 'dais_health_questions');
    const q = query(
      questionsRef, 
      where('card_id', '==', categoryId),
      orderBy('sequence', 'asc')
    );
    const querySnapshot = await getDocs(q);
    
    const questions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return questions;
  } catch (error) {
    throw error;
  }
};

export const saveLeadToFirebase = async (userData, testData, answers) => {
  try {
    
    const leadsRef = collection(db, 'leads');
    const result = await addDoc(leadsRef, {
      ...userData,
      testId: testData.id,
      testTitle: testData.title,
      answers: answers,
      createdAt: new Date(),
      source: userData.comoTeEnteraste
    });
    
    return { success: true, id: result.id };
  } catch (error) {
    throw error;
  }
};

export async function checkEmailAvailability(email) {
  try {
    
    const usersRef = collection(db, 'dais_user');
    const q = query(usersRef, where('email', '==', email.toLowerCase().trim()));
    const querySnapshot = await getDocs(q);
    
    const isAvailable = querySnapshot.empty;
    
    return isAvailable;
  } catch (error) {
    return true; 
  }
}

export async function checkPhoneAvailability(phone) {
  try {
    
    const usersRef = collection(db, 'dais_user');
    const q = query(usersRef, where('phone', '==', phone.trim()));
    const querySnapshot = await getDocs(q);
    
    const isAvailable = querySnapshot.empty;
    
    return isAvailable;
  } catch (error) {
    return true;
  }
}

export async function getCountriesFromFirebase() {
  try {
    const countriesRef = collection(db, 'res_country');
    const querySnapshot = await getDocs(countriesRef);
    
    const countries = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: data.id,
        firestoreId: doc.id,
        code: data.code,
        name: data.name,
        phone_code: data.phone_code,
        sequence: data.sequence || 999
      };
    });
    
    return countries.sort((a, b) => a.sequence - b.sequence);
  } catch (error) {
    return [];
  }
}

/**
 * Busca el perfil completo del usuario en la colección dais_user por su firebase_uid
 */
/**
 * Sign-in social (Google o Apple) vía popup de Firebase. Mismo contrato de
 * salida que createUserInFirebase/loginUserInFirebase para reusar los flujos:
 * { success, firebaseUid, firebaseToken, email, displayName } o
 * { success: false, error, errorCode } ('cancelled' cuando cerró el popup).
 */
export const socialSignInFirebase = async (providerName) => {
  const provider =
    providerName === 'google'
      ? new GoogleAuthProvider()
      : new OAuthProvider('apple.com');
  if (providerName === 'apple') {
    provider.addScope('email');
    provider.addScope('name');
  }
  try {
    const credential = await signInWithPopup(auth, provider);
    const user = credential.user;
    const email = (user.email || '').toLowerCase().trim();
    if (!email) {
      return {
        success: false,
        error: 'No recibimos tu correo del proveedor. Intenta de nuevo compartiendo tu correo.',
        errorCode: 'no-email'
      };
    }
    // Doc espejo users/{uid}; merge para no pisar logins previos.
    try {
      await setDoc(doc(db, 'users', user.uid), { uid: user.uid, email }, { merge: true });
    } catch (err) {
      console.warn('[socialSignInFirebase] setDoc users/{uid} falló (no bloquea):', err);
    }
    const firebaseToken = await user.getIdToken();
    return {
      success: true,
      firebaseUid: user.uid,
      firebaseToken,
      email,
      displayName: (user.displayName || '').trim()
    };
  } catch (error) {
    const code = error?.code || '';
    if (
      code === 'auth/popup-closed-by-user' ||
      code === 'auth/cancelled-popup-request' ||
      code === 'auth/user-cancelled'
    ) {
      return { success: false, error: '', errorCode: 'cancelled' };
    }
    if (code === 'auth/account-exists-with-different-credential') {
      return {
        success: false,
        error: 'Ese correo ya tiene cuenta con otro método. Inicia sesión con tu correo y contraseña.',
        errorCode: code
      };
    }
    if (code === 'auth/popup-blocked') {
      return {
        success: false,
        error: 'Tu navegador bloqueó la ventana de acceso. Permite popups e intenta de nuevo.',
        errorCode: code
      };
    }
    return {
      success: false,
      error: error?.message || 'No pudimos completar el acceso.',
      errorCode: code
    };
  }
};

export const fetchDaisUserByFirebaseUid = async (firebaseUid) => {
  try {
    const usersRef = collection(db, 'dais_user');
    const q = query(usersRef, where('firebase_uid', '==', firebaseUid));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const userDoc = querySnapshot.docs[0];
    return {
      firestoreId: userDoc.id,
      ...userDoc.data()
    };
  } catch (error) {
    console.warn('Error fetching dais_user:', error);
    return null;
  }
};

/**
 * Busca las consultas agendadas del usuario en la colección dais_consultation
 * Filtros: user == odooUserId, state == 'scheduled', active == true
 * Orden: id descendente
 * @param {number|string} odooUserId — el ID numérico del usuario en Odoo
 */
export const fetchUserConsultations = async (odooUserId) => {
  try {
    const uid = typeof odooUserId === 'string' ? parseInt(odooUserId, 10) : odooUserId;
    const consultationsRef = collection(db, 'dais_consultation');
    // user is a map, query nested field user.id
    const q = query(consultationsRef, where('user.id', '==', uid));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return [];
    }
    
    const consultations = querySnapshot.docs
      .map(doc => ({
        firestoreId: doc.id,
        ...doc.data()
      }))
      .filter(c => c.state === 'scheduled' && c.active === true)
      .sort((a, b) => (b.id || 0) - (a.id || 0));
    
    return consultations;
  } catch (error) {
    return [];
  }
};

/**
 * Fetches the medical survey (historia clínica) for a given specialty
 * from the dais_survey collection.
 * Filters: specialty.id == specialtyId, type_survey.is_valoration_survey == false
 * @param {number} specialtyId – Odoo specialty ID
 * @returns {object|null} – survey document data or null
 */
export const fetchSurveyBySpecialty = async (specialtyId) => {
  try {
    const surveyRef = collection(db, 'dais_survey');
    const q = query(
      surveyRef,
      where('specialty.id', '==', specialtyId),
      where('type_survey.is_valoration_survey', '==', false)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.warn('[FirebaseService] No survey found for specialty:', specialtyId);
      return null;
    }

    const surveyDoc = querySnapshot.docs[0];
    return {
      firestoreId: surveyDoc.id,
      ...surveyDoc.data()
    };
  } catch (error) {
    console.error('[FirebaseService] fetchSurveyBySpecialty error:', error);
    return null;
  }
};
/**
 * Fetches consultation reasons from the consultation_reason collection
 * filtered by specialty_id.
 * @param {number} specialtyId – Odoo specialty ID
 * @returns {Array} – array of reason objects {id, name, description, ...}
 */
export const fetchConsultationReasons = async (specialtyId) => {
  try {
    const reasonsRef = collection(db, 'consultation_reason');
    const q = query(
      reasonsRef,
      where('specialty_id', '==', specialtyId),
      where('active', '==', true)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return [];
    }

    return querySnapshot.docs
      .map(doc => ({ firestoreId: doc.id, ...doc.data() }))
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  } catch (error) {
    console.error('[FirebaseService] fetchConsultationReasons error:', error);
    return [];
  }
};

/**
 * Lee precios de planes de membresía desde Firestore `dais_membership/<id>`.
 * Odoo sincroniza ahí cada plan (dais.membership._sync_to_firestore con get_model_data),
 * así que `monthly_price` y `price` (total del periodo) vienen del backend.
 *
 * @param {number[]} ids — IDs Odoo de los planes (deben coincidir con los de pago).
 * @returns {Promise<Object>} mapa { [id]: { monthlyPrice, totalPrice, name } } solo
 *   con los planes que se pudieron leer. El caller hace fallback a los valores locales.
 */
export const fetchMembershipPricing = async (ids = [21, 23, 25]) => {
  const result = {};
  await Promise.all(
    ids.map(async (id) => {
      try {
        const snap = await getDoc(doc(db, 'dais_membership', String(id)));
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const monthly = Number(data.monthly_price);
        const total = Number(data.price);
        result[id] = {
          monthlyPrice: Number.isFinite(monthly) && monthly > 0 ? monthly : null,
          totalPrice: Number.isFinite(total) && total > 0 ? total : null,
          name: data.name || '',
        };
      } catch (error) {
        console.warn(`[FirebaseService] fetchMembershipPricing ${id} error:`, error);
      }
    })
  );
  return result;
};

/**
 * Lista los planes de suscripción visibles desde Firestore `dais_membership`,
 * sincronizados por Odoo. Es la fuente de verdad para los selectores de paquete.
 *
 * Replica el patrón canónico de Flutter (plan_options_page): consulta por
 * `active == true` y filtra `is_shown` EN MEMORIA con default `true`. Así un
 * plan solo se oculta cuando alguien marcó explícitamente is_shown=false en
 * Odoo; un doc que aún no sincronizó ese campo (ausente) se sigue mostrando.
 * NO se consulta `where('is_shown','==',true)` directo porque, mientras Odoo
 * no haya re-sincronizado el campo a Firestore, esa query devuelve 0 docs y la
 * UI cae al fallback hardcodeado.
 *
 * Normaliza cada doc a un shape estable:
 *   { id, odooId, membershipId, name, price (total periodo), monthlyPrice,
 *     durationDays, months, order, isRecommended, isShown, type, features[] }
 *
 * `price`/`monthly_price` pueden venir como string en Firestore → se castean.
 * Los IDs (id == odooId == membershipId) son el membership_id del cargo y NO se
 * alteran. Devuelve [] si falla (el caller hace fallback a getMembershipPlans).
 */
export const fetchVisibleMemberships = async () => {
  try {
    const q = query(collection(db, 'dais_membership'), where('active', '==', true));
    const snap = await getDocs(q);
    const list = snap.docs
      .map((d) => {
        const data = d.data() || {};
        const id = Number(d.id);
        const type = data.type || '';
        const price = Number(data.price) || 0;
        const monthlyPrice = Number(data.monthly_price) || 0;
        const durationDays = Number(data.duration) || 0;
        const months = durationDays > 0 ? Math.max(1, Math.round(durationDays / 30)) : 1;
        // Default true: la ocultación es opt-in desde Odoo (is_shown === false).
        const isShown = data.is_shown !== false;

        // `details` es un array con un blob de texto: primera línea + bullets "- ".
        let features = [];
        const det = Array.isArray(data.details) ? data.details : [];
        const desc = det[0]?.description || det[0]?.fields?.description || '';
        if (desc && typeof desc === 'string') {
          features = desc
            .split('\n')
            .map((s) => s.replace(/[​]/g, '').replace(/^[-•\s]+/, '').trim())
            .filter(Boolean);
        }

        return {
          id,
          odooId: id,
          membershipId: id,
          name: data.name || 'Membresía',
          price,
          monthlyPrice: monthlyPrice > 0 ? monthlyPrice : (months > 0 ? Math.round(price / months) : price),
          durationDays,
          months,
          order: Number(data.order) || 0,
          isRecommended: !!data.is_recommended,
          isShown,
          type,
          features,
        };
      })
      // Solo suscripciones visibles con precio válido. is_shown se respeta cuando
      // está presente (false oculta); ausente => visible (Flutter-parity).
      .filter((m) => m.isShown && m.type === 'subscription' && m.price > 0);
    list.sort((a, b) => a.order - b.order);
    return list;
  } catch (error) {
    console.warn('[FirebaseService] fetchVisibleMemberships error:', error);
    return [];
  }
};

/** Etiqueta de periodo a partir de meses: 1→/mes, 12→/año, otro→/N meses. */
export const periodLabel = (months) => {
  if (months === 1) return '/mes';
  if (months === 12) return '/año';
  return `/${months} meses`;
};

// Exportar instancias
export { db, auth, collection, getDocs, query, where, getDoc };